// =============================================================================
// resend-activation-error.ts — Interprétation PAR CODE MACHINE du renvoi de lien d'activation.
// =============================================================================
// CONTACTS-A04 (slice 4) : le renvoi du lien d'activation partage la MÊME sémantique d'erreur sur
// les DEUX écrans (fournisseur §3.7 / admin §2.2). Le back centralise la traduction dans
// `resend-activation-error.mapper.ts` :
//   • ActivationResendRateLimitError         → 429 (plafond 3/24h) ;
//   • ActivationResendDeniedError 'already-active' → 409 (déjà activé, rien à renvoyer) ;
//   • ActivationResendDeniedError 'unknown'  → 404 (contact absent / hors tenant, fail-closed).
//
// Côté UI, on discrimine PAR CODE (jamais le message serveur brut) :
//   - fournisseur : SupplierTeamApiError.kind ('rate-limited' | 'conflict' | 'not-found') ;
//   - admin       : SupplierApiError.status (429 | 409 | 404).
// Ce module normalise les deux en un `ResendFailureCode` et fournit le libellé UX figé + la règle
// de refetch (le 409 refète le statut réel du contact, désormais « Activé »).

import { SupplierApiError } from '@/api/supplier-client';
import { SupplierTeamApiError } from '@/api/supplier-team-client';

/** Statut du lien (union canonique partagée par les deux écrans — miroir `TokenActivationStatus`). */
export type TokenActivationStatus = 'Envoye' | 'Expire' | 'Active';

/** Code machine normalisé d'un échec de renvoi de lien d'activation. */
export type ResendFailureCode = 'rate-limited' | 'already-active' | 'not-found' | 'generic';

/** Normalise une erreur de renvoi (fournisseur OU admin) en code machine, sans lire le message brut. */
export function resendFailureCode(error: unknown): ResendFailureCode {
  if (error instanceof SupplierTeamApiError) {
    switch (error.kind) {
      case 'rate-limited':
        return 'rate-limited';
      case 'conflict':
        return 'already-active';
      case 'not-found':
        return 'not-found';
      default:
        return 'generic';
    }
  }
  if (error instanceof SupplierApiError) {
    switch (error.status) {
      case 429:
        return 'rate-limited';
      case 409:
        return 'already-active';
      case 404:
        return 'not-found';
      default:
        return 'generic';
    }
  }
  return 'generic';
}

/** Libellé UX FIGÉ par code (jamais le message serveur brut). */
export function resendFailureMessage(code: ResendFailureCode): string {
  switch (code) {
    case 'rate-limited':
      return 'Limite de renvois atteinte (3 par 24h). Réessayez plus tard.';
    case 'already-active':
      return 'Ce contact a déjà activé son accès.';
    case 'not-found':
      return 'Contact introuvable.';
    case 'generic':
      return 'Le renvoi du lien a échoué. Réessayez.';
  }
}

/**
 * Le 409 « déjà activé » signifie que le statut affiché est périmé : il faut refetcher pour
 * refléter l'état réel (« Activé »). Les hooks invalident systématiquement le statut sur `onSettled`,
 * mais cette règle explicite documente l'intention côté écran.
 */
export function resendShouldRefetch(code: ResendFailureCode): boolean {
  return code === 'already-active';
}

/** Affordance du bouton « Renvoyer le lien » selon le statut (Écart 1 — DBU §3.7 prévaut). */
export interface ResendAffordance {
  /** Le bouton est-il rendu ? (masqué pour « Activé » et `null`). */
  readonly render: boolean;
  /** Le bouton est-il désactivé ? (« Envoyé » = lien encore valide, non expiré). */
  readonly disabled: boolean;
  /** Raison de désactivation, annoncée (aria/title), ou `null` si actif. */
  readonly disabledReason: string | null;
}

/**
 * Affordance FRONT du bouton « Renvoyer le lien », alignée sur la garde SERVEUR #30 « Expiré-only »
 * (A0-MINEURS-SCOPE h, « durcir serveur léger ») : `resendForContact` REFUSE désormais le renvoi d'un
 * lien encore « Envoyé » (ActivationResendDeniedError 'still-valid' → 409). L'UI grise donc le bouton
 * pour « Envoyé » en cohérence avec le serveur (défense en profondeur, jamais l'unique frontière) :
 *   • Expiré → bouton ACTIF (visible + cliquable) ;
 *   • Envoyé → bouton VISIBLE mais DÉSACTIVÉ (lien encore valide, non expiré — le serveur refuserait) ;
 *   • Activé → bouton MASQUÉ (rien à renvoyer) ;
 *   • null (aucune identité émise) → bouton MASQUÉ.
 */
export function resendAffordance(status: TokenActivationStatus | null): ResendAffordance {
  switch (status) {
    case 'Expire':
      return { render: true, disabled: false, disabledReason: null };
    case 'Envoye':
      return {
        render: true,
        disabled: true,
        disabledReason: "Le lien d'activation est encore valide (non expiré).",
      };
    case 'Active':
    case null:
      return { render: false, disabled: true, disabledReason: null };
  }
}
